import {
  ArrowGo,
  Calendar,
  Check,
  EmoTrophy,
  Heart,
  HeartFilled,
  Share,
  UserFilled,
} from "@assets"
import { Head, Link, router } from "@inertiajs/react"
import { Clock, Link as LinkIcon, Share2, Ticket, Users } from "lucide-react"

// import { useState } from 'react'
import { AuthButton } from "@/components/auth-button"
import { ContentActionsMenu } from "@/components/content-actions-menu"
import { PageHeader } from "@/components/page-header"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { useFavorites } from "@/hooks/use-favorites"
import AppLayout from "@/layouts/app-layout"
import {
  businessPath,
  raffleEditPath,
  raffleParticipantPath,
  rafflePath,
  rafflesPath,
  userPath,
} from "@/routes"

interface Raffle {
  id: number
  title: string
  description?: string
  prize_description?: string
  terms_and_conditions?: string
  ticket_price?: number
  max_tickets?: number
  tickets_sold?: number
  max_participants?: number
  start_date?: string
  end_date?: string
  winner_announced_date?: string
  winner?: {
    id: number
    name: string
    avatar_url?: string
  }
  // Backend uses 'active' | 'ended' | 'cancelled' | 'draft'
  status?: "active" | "ended" | "cancelled" | "draft" | "completed"
  is_active?: boolean
  is_upcoming?: boolean
  has_ended?: boolean
  is_favorited?: boolean
  favorites_count?: number
  image_url?: string
  image?: string
  sponsor?: {
    id: number
    business_name: string
    avatar_url?: string
    is_verified?: boolean
    rating?: number
    total_reviews?: number
    owner_id?: number
  }
  created_at: string
  user_tickets?: number
}

interface ViralCampaign {
  id: number
  slug: string
  title: string
  base_entries: number
  entries_per_referral: number
  status: string
  is_active?: boolean
}

interface ViralParticipant {
  id: number
  referral_code: string
  total_entries_cached: number
  base_entries: number
  referral_entries: number
  bonus_entries: number
}

interface Participant {
  id: number
  name: string
  avatar_url?: string
  tickets_count: number
  joined_date?: string
}

interface Props {
  raffle: Raffle
  // These are not currently provided by the controller; make optional with safe defaults
  participants?: Participant[]
  recent_winners?: {
    id: number
    raffle_title: string
    winner_name: string
    winner_avatar?: string
    won_date: string
    prize: string
  }[]
  participants_count?: number
  is_participating?: boolean
  user_participation?: { id: number }
  can_edit?: boolean
  // Viral integration
  viral_campaign?: ViralCampaign | null
  viral_participant?: ViralParticipant | null
}

export default function RaffleShow({
  raffle,
  participants,
  recent_winners,
  participants_count,
  is_participating,
  user_participation,
  can_edit,
  viral_campaign,
  viral_participant,
}: Props) {
  const ticketQuantity = 1
  const {
    isFavorited: getStoreFavorite,
    setFavorite: setStoreFavorite,
    getFavoritesCount: getStoreCount,
    setFavoritesCount: setStoreCount,
  } = useFavorites()

  // Use store value if available, otherwise fallback to props
  const storedFavorite = getStoreFavorite("raffle", raffle.id)
  const storedCount = getStoreCount("raffle", raffle.id)
  const actualFavorited = storedFavorite ?? raffle.is_favorited
  const actualFavoritesCount = storedCount ?? raffle.favorites_count ?? 0

  const handleFavorite = () => {
    const newValue = !actualFavorited
    const newCount = newValue
      ? actualFavoritesCount + 1
      : Math.max(0, actualFavoritesCount - 1)

    // Sync with global store immediately for optimistic update
    setStoreFavorite("raffle", raffle.id, newValue)
    setStoreCount("raffle", raffle.id, newCount)

    router.post(
      `${rafflePath(raffle.id)}/favorite`,
      {},
      {
        preserveScroll: true,
        onError: () => {
          // Revert on error
          setStoreFavorite("raffle", raffle.id, !newValue)
          setStoreCount(
            "raffle",
            raffle.id,
            newValue ? Math.max(0, newCount - 1) : newCount + 1,
          )
        },
      },
    )
  }

  const handleBuyTickets = () => {
    router.post(
      `/sorteos/${raffle.id}/participantes`,
      {
        tickets_count: ticketQuantity,
      },
      {
        preserveScroll: true,
      },
    )
  }

  const handleCancelParticipation = (participantId?: number) => {
    if (!participantId) return
    router.delete(raffleParticipantPath(raffle.id, participantId), {
      preserveScroll: true,
    })
  }

  const handleDelete = () => {
    if (!confirm("¿Eliminar este sorteo? Esta acción no se puede deshacer."))
      return
    router.delete(rafflePath(raffle.id), {
      preserveScroll: false,
      onSuccess: () => {
        router.visit(rafflesPath())
      },
    })
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
    }).format(price)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("es-ES", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getTimeRemaining = () => {
    const now = new Date()
    // If upcoming, count down to start_date. Otherwise, to end_date.
    const targetIso = raffle.is_upcoming ? raffle.start_date : raffle.end_date
    if (!targetIso) return null
    const target = new Date(targetIso)
    const diffInMs = target.getTime() - now.getTime()

    if (diffInMs <= 0) return { days: 0, hours: 0, minutes: 0 }

    const days = Math.floor(diffInMs / (1000 * 60 * 60 * 24))
    const hours = Math.floor(
      (diffInMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    )
    const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60))

    return { days, hours, minutes }
  }

  const getStatusBadge = () => {
    switch (raffle.status) {
      case "active":
        return <Badge variant="default">Activo</Badge>
      case "ended":
        return <Badge variant="secondary">Finalizado</Badge>
      case "cancelled":
        return <Badge variant="destructive">Cancelado</Badge>
      default:
        return null
    }
  }

  const hasTicketNumbers =
    typeof raffle.ticket_price === "number" &&
    typeof raffle.max_tickets === "number" &&
    typeof raffle.tickets_sold === "number"
  const hasCapacity = typeof raffle.max_participants === "number"
  const safeParticipants: Participant[] = participants ?? []
  const safeRecentWinners = recent_winners ?? []
  const participantsCount: number =
    participants_count ?? safeParticipants.length
  const capacity = hasCapacity ? raffle.max_participants! : 0
  const timeRemaining = getTimeRemaining()
  const isRaffleActive = !!raffle.is_active
  const isRaffleUpcoming = !!raffle.is_upcoming
  const showParticipationSection = isRaffleActive || isRaffleUpcoming
  const remainingCapacity = hasCapacity
    ? Math.max(0, capacity - participantsCount)
    : 0
  const isTimeZero =
    !!timeRemaining &&
    timeRemaining.days === 0 &&
    timeRemaining.hours === 0 &&
    timeRemaining.minutes === 0

  // Viral helpers (safe for SSR)
  const getReferralLink = () => {
    if (typeof window === "undefined") return ""
    const baseRaffleUrl = `${window.location.origin}/sorteos/${raffle.id}`
    if (viral_campaign && viral_participant) {
      return `${baseRaffleUrl}?ref=${viral_participant.referral_code}`
    }
    return baseRaffleUrl
  }

  const handleViralShare = (platform: "whatsapp" | "copy_link") => {
    if (!viral_campaign) return

    const referralLink = getReferralLink()

    if (platform === "whatsapp") {
      const message = encodeURIComponent(
        `¡Estoy participando en un sorteo en CurrAR! Registrate y ayudame a sumar más chances en este sorteo \n${referralLink}`,
      )
      window.open(`https://wa.me/?text=${message}`, "_blank")
    } else if (platform === "copy_link") {
      if (!referralLink) return
      if (
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function"
      ) {
        void navigator.clipboard.writeText(referralLink)
      }
    }

    void router.post(
      `/viral/campaigns/${viral_campaign.id}/shares`,
      {
        platform,
        message_template_used: "from_raffle_show",
        return_to: window.location.pathname,
      },
      { preserveScroll: true, preserveState: true },
    )
  }

  const handleShare = async () => {
    try {
      const url = getReferralLink()
      const title = raffle?.title ?? "Sorteo"
      if (navigator.share) {
        await navigator.share({ title, url })
      } else if (
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function"
      ) {
        await navigator.clipboard.writeText(url)
      }
    } catch {
      // no-op
    }
    if (viral_campaign) {
      void router.post(
        `/viral/campaigns/${viral_campaign.id}/shares`,
        {
          platform: "copy_link",
          message_template_used: "from_raffle_header",
          return_to: window.location.pathname,
        },
        { preserveScroll: true, preserveState: true },
      )
    }
  }
  // Image carousel removed (single image only)
  return (
    <AppLayout
      showEditButton={can_edit}
      showDeleteButton={can_edit}
      showBackButton={false}
      showShareButton={false}
      showFavoriteButton={false}
      onEdit={() => router.visit(raffleEditPath(raffle.id))}
      onDelete={handleDelete}
    >
      <Head title={raffle?.title ?? "Sorteo"} />

      <div className="mx-0 max-w-6xl space-y-4 py-4 sm:mx-auto md:mx-auto md:space-y-6 md:p-6 lg:mx-auto xl:mx-auto">
        {/* Desktop Navigation */}
        <div className="hidden md:block">
          <PageHeader
            showBackButton={false}
            showEdit={can_edit}
            showDelete={can_edit}
            showShare={false}
            showFavorite={false}
            editHref={raffleEditPath(raffle.id)}
            onDelete={handleDelete}
          />
        </div>
        <div className="px-4">
          <div className="text-primary flex flex-wrap items-center gap-2 text-[13px] font-medium tracking-[0.13px]">
            <button
              onClick={() => window.history.back()}
              className="hover:underline"
            >
              <div className="flex items-center gap-1">
                <ArrowGo className="text-primary size-4 rotate-180 fill-current" />
                Sorteos
              </div>
            </button>
          </div>
          <div className="flex items-start justify-between gap-2">
            <h1 className="text-chat-5 mt-[14px] text-[21px] leading-[28px] font-semibold tracking-[0px] break-words">
              {raffle.title}
            </h1>
            <ContentActionsMenu
              contentType="Raffle"
              contentId={raffle.id}
              authorId={raffle.sponsor?.owner_id ?? 0}
              authorName={raffle.sponsor?.business_name ?? "Sponsor"}
            />
          </div>
          <div className="flex items-center gap-[14px]">
            {/* {raffle.ticket_price > 0  ? (
              <span className="text-primary text-[18px] leading-[27px] tracking-[0.18px] font-semibold h-[31px] rounded-[10px] bg-card px-[8px] py-[3px] whitespace-nowrap">€ {raffle.ticket_price}</span>
            ) : (
              <span className="bg-secondary text-white inline-flex w-fit justify-center items-center px-[7px] py-[2px] text-[10px] tracking-[0.57px] leading-[14px] h-[17px] rounded-[8px]">
                GRATIS
              </span>
            )} */}
            <div className="flex items-center gap-2">
              <Calendar className="fill-muted-foreground h-4 w-4" />
              <span className="text-muted-foreground h-[19px] text-[13px] leading-[20px] tracking-[0.2px]">
                {raffle.start_date && formatDate(raffle.start_date)}
              </span>
            </div>
          </div>

          <div className="text-muted-foreground mt-[11px] flex flex-wrap items-center gap-[16px] text-sm">
            <div className="flex items-center">
              <Users className="fill-muted-foreground mr-1 h-4.5 w-auto" />
              <span className="text-muted-foreground h-[19px] text-[13px] leading-[20px] tracking-[0.20px]">
                {safeParticipants.length} participantes
              </span>
            </div>
            {capacity && (
              <div className="flex items-center gap-1">
                <div className="bg-active h-[8px] w-[8px] rounded-full" />
                <span className="text-card-foreground h-[17px] text-[12px] leading-[18px] font-medium tracking-[0.24px]">
                  {safeParticipants.length === capacity
                    ? "Completo"
                    : `${capacity - safeParticipants.length} plazas disponibles`}
                </span>
              </div>
            )}
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-[14px]">
            <div className="flex items-center gap-[4px]">
              <AuthButton
                variant="ghost"
                size="icon"
                className="h-[14px] w-[16px]"
                onClick={() => void handleFavorite()}
                authAction="register"
              >
                {actualFavorited ? (
                  <HeartFilled className="text-destructive size-4.5 w-auto fill-current" />
                ) : (
                  <Heart className="size-4.5 w-auto" />
                )}
                <span className="sr-only">
                  {actualFavorited
                    ? "Quitar de favoritos"
                    : "Agregar a favoritos"}
                </span>
              </AuthButton>
              <span className="text-muted-foreground text-[13px] tracking-[0.20px]">
                {actualFavoritesCount}
              </span>
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="h-[20px] w-[20px]"
              onClick={() => void handleShare()}
            >
              <Share className="size-5" />
              <span className="sr-only">Compartir</span>
            </Button>
          </div>
        </div>
        <div className="grid gap-4 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Images */}
            {raffle.image && (
              <div className="space-y-4">
                <div className="bg-images relative aspect-video overflow-hidden">
                  <img
                    src={raffle.image}
                    alt={raffle.title}
                    className="h-full w-full object-cover"
                  />
                  {/* Status Overlay */}
                  <div className="absolute top-4 right-4">
                    {getStatusBadge()}
                  </div>
                </div>
              </div>
            )}

            {/* Raffle Info */}
            {raffle.description && (
              <div className="border-chart-4 w-full border-t-[1px] px-4 py-[20px]">
                <CardTitle className="text-chart-5 pb-[5px] text-[14px] leading-[21px] font-semibold tracking-[0.14px]">
                  Descripción del evento
                </CardTitle>
                <div className="prose prose-sm ext-[13px] text-chart-5 max-w-none overflow-hidden leading-[20px] tracking-[0.2px] break-words">
                  {raffle.description}
                </div>
              </div>
            )}
            {/* Organizer Info */}
            {raffle.sponsor && (
              <div className="border-chart-4 w-full border-t-[1px] px-4 py-[20px]">
                <CardTitle className="text-chart-5 pb-[5px] text-[14px] leading-[21px] font-semibold tracking-[0.14px]">
                  Organizador
                </CardTitle>
                <div className="flex items-center gap-4">
                  <div className="bg-card flex h-[60px] w-[60px] flex-shrink-0 items-center justify-center rounded-full">
                    <Avatar className="h-[54px] w-[54px]">
                      <AvatarImage
                        src={raffle.sponsor.avatar_url ?? undefined}
                        alt={raffle.sponsor.business_name}
                      />
                      <AvatarFallback>
                        <UserFilled className="size-9 fill-white" />
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <div>
                    <Link
                      href={businessPath(raffle.sponsor.id)}
                      className="flex items-center gap-2"
                    >
                      <span className="text-chart-5 text-[14px] leading-[21px] font-semibold tracking-[0px]">
                        {raffle.sponsor.business_name}
                      </span>
                      {raffle.sponsor.is_verified && (
                        <Check className="fill-primary h-4.5 w-4.5" />
                      )}
                    </Link>
                    <Link
                      href={businessPath(raffle.sponsor.id)}
                      className="text-chart-3 h-[21px] text-[12px] leading-[18px] font-medium tracking-[0.18px]"
                    >
                      Ver Perfil
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Reglas del sorteo (HTML) */}
            {raffle?.terms_and_conditions && (
              <div className="border-chart-4 w-full border-t-[1px] px-4 pt-[20px]">
                <CardTitle className="text-chart-5 pb-[5px] text-[14px] leading-[21px] font-semibold tracking-[0.14px]">
                  Reglas del sorteo
                </CardTitle>
                {raffle?.prize_description && (
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <div className="bg-card flex h-[32px] w-[32px] items-center justify-center rounded-full p-1">
                        <img
                          src={EmoTrophy}
                          alt="Premio"
                          className="h-full w-auto"
                        />
                      </div>
                      <span className="font-medium">
                        Premio: {raffle.prize_description}
                      </span>
                    </div>
                  </div>
                )}
                <div
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: raffle.terms_and_conditions,
                  }}
                />
              </div>
            )}

            {/* Winner Announcement */}
            {raffle?.winner && (
              <Card className="border-secondary bg-secondary/10">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <div className="bg-card flex h-[32px] w-[32px] items-center justify-center rounded-full p-1">
                      <img
                        src={EmoTrophy}
                        alt="Premio"
                        className="h-full w-auto"
                      />
                    </div>
                    ¡Ganador anunciado!
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center space-x-3">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={raffle.winner.avatar_url} />
                      <AvatarFallback>
                        <UserFilled className="size-8 fill-white" />
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <Link
                        href={userPath(raffle.winner.id)}
                        className="text-foreground font-medium"
                      >
                        {raffle.winner.name}
                      </Link>
                      <div className="text-muted-foreground text-sm">
                        Ganador del sorteo •{" "}
                        {raffle.winner_announced_date &&
                          formatDate(raffle.winner_announced_date)}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="bg-card min-w-0 px-4 py-[20px]">
            {/* Purchase Section */}
            {showParticipationSection && (
              <div>
                <CardTitle className="flex items-center">
                  <Ticket className="mr-2 h-5 w-5" />
                  Participar
                </CardTitle>
                <CardContent className="space-y-4">
                  {is_participating ? (
                    <div className="space-y-3 text-center text-sm">
                      <div className="d text-center text-sm">
                        Ya estás participando -{" "}
                        {viral_participant?.total_entries_cached ?? 1} chances
                      </div>

                      {viral_campaign && (
                        <div className="text-muted-foreground space-y-2">
                          <div className="text-sm">
                            Compartí este sorteo y sumá más chances por cada
                            amigo que se registre.
                          </div>
                          {viral_campaign?.entries_per_referral ? (
                            <div className="text-muted-foreground text-xs">
                              +{viral_campaign.entries_per_referral} por cada
                              referido
                            </div>
                          ) : null}
                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViralShare("whatsapp")}
                            >
                              <Share2 className="mr-2 h-4 w-4" /> WhatsApp
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViralShare("copy_link")}
                            >
                              <LinkIcon className="mr-2 h-4 w-4" /> Copiar link
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : typeof raffle.ticket_price === "number" &&
                    raffle.ticket_price > 0 ? (
                    <div className="text-center">
                      <div className="text-primary mb-1 text-2xl font-bold">
                        {formatPrice(raffle.ticket_price)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-muted-foreground text-center text-sm">
                      Participación gratuita
                    </div>
                  )}

                  <Separator />

                  {typeof raffle.ticket_price === "number" &&
                    raffle.ticket_price > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Total:</span>
                        <span className="text-lg font-bold">
                          {formatPrice(raffle.ticket_price)}
                        </span>
                      </div>
                    )}

                  {is_participating ? (
                    <Button
                      className="w-full"
                      variant="outline"
                      size="lg"
                      onClick={() =>
                        handleCancelParticipation(user_participation?.id)
                      }
                    >
                      Cancelar participación
                    </Button>
                  ) : (
                    <AuthButton
                      className="w-full"
                      size="lg"
                      onClick={handleBuyTickets}
                      disabled={
                        !isRaffleActive ||
                        (hasTicketNumbers &&
                          (raffle.tickets_sold ?? 0) >=
                            (raffle.max_tickets ?? 0)) ||
                        (hasCapacity && remainingCapacity <= 0)
                      }
                      authAction="register"
                    >
                      {!isRaffleActive && isRaffleUpcoming
                        ? "Disponible al iniciar"
                        : remainingCapacity <= 0
                          ? "Cupo lleno"
                          : "Participar"}
                    </AuthButton>
                  )}
                  {(hasTicketNumbers || hasCapacity) && (
                    <div className="text-muted-foreground mt-[13px] flex items-center justify-center text-[13px] leading-[20px] tracking-[0.2px]">
                      <span className="text-muted-foreground">
                        Participantes:
                      </span>
                      <span className="text-chart-5 font-semibold">
                        {participantsCount} / {capacity}
                      </span>
                    </div>
                  )}
                </CardContent>
                {timeRemaining && (
                  <div>
                    <CardTitle className="flex items-center">
                      <Clock className="mr-2 h-5 w-5" />
                      {isTimeZero
                        ? "Finalizado"
                        : raffle.is_upcoming
                          ? "Comienza en"
                          : "Tiempo restante"}
                    </CardTitle>
                    {!isTimeZero && (
                      <CardContent>
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div>
                            <div className="text-primary text-2xl font-bold">
                              {timeRemaining.days}
                            </div>
                            <div className="text-muted-foreground text-xs">
                              días
                            </div>
                          </div>
                          <div>
                            <div className="text-primary text-2xl font-bold">
                              {timeRemaining.hours}
                            </div>
                            <div className="text-muted-foreground text-xs">
                              horas
                            </div>
                          </div>
                          <div>
                            <div className="text-primary text-2xl font-bold">
                              {timeRemaining.minutes}
                            </div>
                            <div className="text-muted-foreground text-xs">
                              min
                            </div>
                          </div>
                        </div>
                        <Separator className="my-3" />
                        <div className="text-muted-foreground text-center text-sm">
                          {raffle.is_upcoming && raffle.start_date && (
                            <span>
                              Comienza: {formatDate(raffle.start_date)}
                            </span>
                          )}
                          {!raffle.is_upcoming &&
                            raffle.end_date &&
                            !isTimeZero && (
                              <span>
                                Finaliza: {formatDate(raffle.end_date)}
                              </span>
                            )}
                        </div>
                      </CardContent>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Recent Participants */}
          {safeParticipants.length > 0 && (
            <div className="border-chart-4 w-full border-t-[1px] px-4 py-[20px]">
              <CardTitle className="text-chart-5 pb-[5px text-[14px] leading-[21px] font-semibold tracking-[0.14px]">
                {" "}
                {safeParticipants.length} Participantes
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center -space-x-2">
                  {safeParticipants.slice(0, 5).map((participant, index) => (
                    <div
                      key={participant.id}
                      className="bg-card relative flex h-[44px] w-[44px] flex-shrink-0 items-center justify-center rounded-full"
                      style={{ zIndex: index }}
                    >
                      <Avatar className="h-[40px] w-[40px]">
                        <AvatarImage
                          src={participant.avatar_url ?? undefined}
                          alt={participant.name}
                        />
                        <AvatarFallback>
                          <UserFilled className="size-9 fill-white" />
                        </AvatarFallback>
                      </Avatar>
                    </div>
                  ))}
                </div>
                {safeParticipants.length > 5 && (
                  <div className="text-muted-foreground h-[19px] text-[13px] leading-[20px] tracking-[0.2px]">
                    + {safeParticipants.length - 5}
                    {/* <span className="ml-1 text-chart-3 text-[12px] leading-[18px] tracking-[0.18px] font-medium">Ver Asistentes</span> */}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Recent Winners */}
          {safeRecentWinners.length > 0 && (
            <div className="border-chart-4 w-full border-t-[1px] px-4 py-[20px]">
              <CardTitle className="text-chart-5 flex items-center gap-2 pb-[5px] text-[14px] leading-[21px] font-semibold tracking-[0.14px]">
                <div className="bg-card flex h-[32px] w-[32px] items-center justify-center rounded-full p-1">
                  <img src={EmoTrophy} alt="Premio" className="h-full w-auto" />
                </div>
                Ganadores recientes
              </CardTitle>
              <div className="space-y-3">
                {safeRecentWinners.slice(0, 3).map((winner) => (
                  <div key={winner.id} className="flex items-center space-x-2">
                    <div className="bg-card flex h-[60px] w-[60px] flex-shrink-0 items-center justify-center rounded-full">
                      <Avatar className="h-[54px] w-[54px]">
                        <AvatarImage
                          src={winner.winner_avatar ?? undefined}
                          alt={winner.winner_name}
                        />
                        <AvatarFallback>
                          <UserFilled className="size-9 fill-white" />
                        </AvatarFallback>
                      </Avatar>
                    </div>
                    <div className="flex-1">
                      <div className="line-clamp-1 text-sm font-medium">
                        {winner.raffle_title}
                      </div>
                      <Link
                        href={userPath(winner.id)}
                        className="text-muted-foreground text-xs"
                      >
                        Ganador: {winner.winner_name}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
